/* eslint-disable react/no-unescaped-entities */
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import GenericCard from '@/components/GenericCard';
import Search from '@/components/Search';
import SingleSelectFilter from '@/components/SingleSelectFilter';
import { ComponentProps } from '@components/ComponentProps';
import PageMeta from "@components/PageMeta";

import {
  SearchSuggestion,
  SearchSuggestionsWrapper,
  StyledBackground,
  StyledDefaultWrapper,
  StyledNoResultsWrapper,
  StyledPagination,
  StyledResultsList,
  StyledResultsTopWrapper,
  StyledResultsWrapper,
  StyledSearchPage,
  StyledTopWrapper
} from './Search.styles';

import { useMobile } from "@lib/hooks/useMobile";
import { changePage, ListingGridContainer } from '@lib/listing';

import { SearchPageFragment } from '@/__generated__/graphql';
import Link from 'next/link';

export type SearchPageProps = ComponentProps<SearchPageFragment> & {
  pageUrl: string;
};

export default function SearchPage({
  title,
  subtitle,
  mostSearchedFor,
  searchResults,
  pageUrl,
  metatags,
}: SearchPageProps): ReactElement<any> {

  const [searchOpen, setSearchOpen] = useState(false);
  const resultSection = useRef<HTMLDivElement|null>(null);
  const isMobile = useMobile(1200);

  const sortByOptions = useMemo(() => {
    return [{
    value: '',
    label: 'Most relevant'
  },
  {
    value: 'DESC',
    label: 'Newest first'
  },
  {
    value: 'ASC',
    label: 'Oldest first'
  }]
  },  []);

  // Get the sortBy param from current url
  const appliedSortBy = useMemo(() => {
    const params = new URL(pageUrl).searchParams;
    const sortby = params.get('sortBy');
    return sortByOptions.filter((option) => option.value === sortby)[0]?.label;
  }, [pageUrl, sortByOptions]);

  // Get the keywords param from current url
  const currentKeywords = useMemo(() => {
    const params = new URL(pageUrl).searchParams;
    return params.get('keywords');
  }, [pageUrl]);

  // Get the page param from current url
  const currentPage = useMemo(() => {
    const params = new URL(pageUrl).searchParams;
    return parseInt(params.get('page') ?? '1');
  }, [pageUrl]);

  // Create a url for a most searched keyword
  const makeKeywordUrl = (keyword: string) => {
    const url = new URL(pageUrl);
    url.searchParams.delete('page');
    url.searchParams.delete('sortBy');
    url.searchParams.set('keywords', keyword);
    return url.href;
  };

  const onChangeSortBy = (value: string) => {
    const url = new URL(pageUrl);
    const origSearchParams = url.searchParams.toString();
    url.searchParams.set('sortBy', value);
    // The filters have changed. Reset pagination to avoid confusing results.
    if (origSearchParams !== url.searchParams.toString()) {
      url.searchParams.delete('page');
    }
    window.location.href = url.href;
  };

  const formattedSortByFilter = {
    title: 'Filter by',
    options: sortByOptions,
    selected: appliedSortBy ?? 'Most relevant',
    onChange: onChangeSortBy,
  };

  // Calculate the index of the first and last result displayed on the page
  let firstResult = 0;
  let lastResult = 0;
  if (searchResults) {
    const { total, pageSize, page } = searchResults;
    firstResult = pageSize * page + 1;
    if ((page + 1) * pageSize >= total) {
      lastResult = total;
    } else {
      lastResult = firstResult + pageSize - 1;
    }
  }

  useEffect(() => {
    if (resultSection.current !== null && appliedSortBy) {
      let headerOffset = isMobile ? 110 : 180;
      let elementPosition = resultSection.current.getBoundingClientRect().top;
      let offsetPosition = elementPosition +  window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  }, [appliedSortBy, isMobile]);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={[]}
        path={pageUrl}
      />
    <StyledSearchPage>
      <StyledBackground>
        <ListingGridContainer>
          <StyledTopWrapper>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <Search
              type="searchPage"
              keywords={currentKeywords || ""}
              open={searchOpen}
              setOpen={setSearchOpen}
              />
          </StyledTopWrapper>
        </ListingGridContainer>
      </StyledBackground>
      <ListingGridContainer ref={resultSection}>
        {/* Default page */}
        {!currentKeywords && (mostSearchedFor && mostSearchedFor.length > 0) && (
          <StyledDefaultWrapper>
            <h2>Most searched</h2>
            <ul>
              {mostSearchedFor!.map((word) => {
                if (!word) return;
                return (
                  <li key={word}>
                    <a href={makeKeywordUrl(word)}>{word}</a>
                  </li>
                );
              })}
            </ul>
          </StyledDefaultWrapper>
        )}
        {/* No search result */}
        {currentKeywords && searchResults?.total === 0 && (
          <>
            {/* Has search suggestions */}
            {searchResults?.didYouMean && searchResults?.didYouMean?.length >= 0 && (
              <SearchSuggestionsWrapper>
                <div className="suggestions-title">Do you mean:</div>
                <div className="suggestions-wrapper">
                  {searchResults.didYouMean.map((suggestion, index) => (
                    <SearchSuggestion key={`suggestion-${index}`}>
                      <Link href={`/search?keywords=${suggestion}`}>{suggestion}</Link>
                    </SearchSuggestion>
                    ))}
                  </div>
              </SearchSuggestionsWrapper>
            )}
            <StyledNoResultsWrapper hasSuggestions={(searchResults?.didYouMean && searchResults?.didYouMean?.length >= 0) !== undefined }>
              <p>
                No Results Found For: <span>“{searchResults.keywords}”</span>
              </p>
            </StyledNoResultsWrapper>
          </>
        )}
        {/* With search result */}
        {searchResults && searchResults.total > 0 && (
          <StyledResultsWrapper>
            <StyledResultsTopWrapper>
              <div>
                <h2>{searchResults.keywords ? `“${searchResults.keywords}”`: `All content`}</h2>
                <p>
                  Displaying {firstResult} - {lastResult} of {searchResults.total}
                </p>
              </div>
              <SingleSelectFilter {...formattedSortByFilter} />
            </StyledResultsTopWrapper>
            <StyledResultsList>
              {searchResults.resultList?.map((result) => (
                <li key={result.id}>
                  <GenericCard
                    {...result}
                    image={{
                      ...result.teaserImage,
                      alt: result.teaserImage.altText,
                      url: result.teaserImage.url,
                    }}
                    description={result.teaserDescription || ""}
                    eyebrow={result.teaserEyebrow ?? undefined}
                    contentType={result.contentType ?? undefined}
                  />
                </li>
              ))}
            </StyledResultsList>
            <StyledPagination
              href={pageUrl}
              totalPages={
                searchResults.totalPages ?? Math.ceil(searchResults.total / searchResults.pageSize)
              }
              currentPage={Math.max(currentPage, 1)}
              siblingCount={3}
              onPageChange={changePage}
            />
          </StyledResultsWrapper>
        )}
      </ListingGridContainer>
    </StyledSearchPage>
    </>
  );
}
